import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@roger/ui-components';
import type {
  BillingReadinessCategory,
  GetBillingCyclesResponse,
} from '../../services/api/types.gen';
import type { DraftReadinessScope } from './ClaimReadinessProvider';
import { ClaimReadinessDetails } from './ClaimReadinessDetails';

/** One of the five fixed readiness positions on a draft claims row. */
export type ClaimReadinessState = 'pass' | 'fail';

export type ClaimReadinessCategoryState = {
  category: BillingReadinessCategory;
  label: string;
  state: ClaimReadinessState;
};

const CATEGORY_LABELS: Record<BillingReadinessCategory, string> = {
  oasis: 'OASIS',
  hipps: 'HIPPS',
  visits: 'Visits',
  orders: 'Orders',
  insurance: 'Insurance',
};

const CATEGORY_ORDER: BillingReadinessCategory[] = [
  'oasis',
  'hipps',
  'visits',
  'orders',
  'insurance',
];

/**
 * The list row's verdict for each fixed category, in the approved order.
 *
 * These are the same predicates the server's grouped Not Ready filter uses, so
 * a row that appears under a selected category always shows that category's X.
 */
// eslint-disable-next-line react-refresh/only-export-components -- exported for the details panel and unit testing
export function getClaimReadinessCategories(
  entry: GetBillingCyclesResponse
): ClaimReadinessCategoryState[] {
  return CATEGORY_ORDER.map(category => ({
    category,
    label: CATEGORY_LABELS[category],
    state: categoryState(category, entry),
  }));
}

function categoryState(
  category: BillingReadinessCategory,
  entry: GetBillingCyclesResponse
): ClaimReadinessState {
  switch (category) {
    case 'oasis':
      // An externally billed prior-episode assessment satisfies the gate
      // without Roger completion/export/signature (EMR-778).
      return entry.all_oasis_correct_state &&
        (entry.payment_oasis_status === 'eligible' ||
          entry.payment_oasis_status === 'external')
        ? 'pass'
        : 'fail';
    case 'hipps':
      // The grouper's inputs (eligible payment OASIS + diagnoses), the same
      // gate the EOE preview's HIPPS check reports (EMR-2355). Never the
      // grouper result, which reads worksheet diagnoses and can differ
      // (EMR-2358). EMR-2395: an invalid diagnosis code on the episode
      // worksheet also fails it; it blocks Create Claim.
      return entry.has_computable_hipps_inputs &&
        entry.has_valid_diagnosis_codes
        ? 'pass'
        : 'fail';
    case 'visits':
      return entry.all_visits_completed && entry.has_billable_visits
        ? 'pass'
        : 'fail';
    case 'orders':
      return entry.all_orders_signed ? 'pass' : 'fail';
    case 'insurance': {
      const mbiFails = entry.is_mbi_required && !entry.is_mbi_valid;
      const pcrFails =
        entry.is_pcr_tracking_number_required &&
        !entry.is_pcr_tracking_number_valid;
      const authorizationFails =
        entry.is_authorization_required && !entry.is_authorization_valid;
      return mbiFails || pcrFails || authorizationFails ? 'fail' : 'pass';
    }
  }
}

const STATE_TEXT: Record<ClaimReadinessState, string> = {
  pass: 'Ready',
  fail: 'Not ready',
};

const StateIcon: React.FC<{ state: ClaimReadinessState }> = ({ state }) => {
  if (state === 'pass') {
    return <Check aria-hidden='true' className='h-3.5 w-3.5 text-green-600' />;
  }
  return <X aria-hidden='true' className='h-3.5 w-3.5 text-red-600' />;
};

/**
 * The five-category readiness cell shared by both draft claims tabs.
 *
 * Each category is a labelled button carrying its check or X, so
 * the verdict is readable without hover and is not conveyed by color alone. A
 * details panel loads only when a category is actually opened and its evidence
 * is needed — passing categories and the whole table render no per-row request.
 *
 * Hover opens after 300ms without stealing focus; click, Enter or Space pins it so
 * its links stay reachable by keyboard and touch; Escape closes it and returns
 * focus to the trigger. Pointer exit never closes a pinned panel. Clicks stop
 * propagating so opening a category never selects the row or creates a claim.
 */
export const ClaimReadinessCell: React.FC<{
  entry: GetBillingCyclesResponse;
  scope: DraftReadinessScope;
}> = ({ entry, scope }) => {
  const categories = getClaimReadinessCategories(entry);
  const [openCategory, setOpenCategory] =
    useState<BillingReadinessCategory | null>(null);
  const [openSource, setOpenSource] = useState<'hover' | 'persistent' | null>(
    null
  );
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors `openSource` for callbacks that run outside the render that set it
  // (Radix's open/close-autofocus handlers).
  const pinned = useRef(false);
  const returnFocusOnClose = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef<
    Partial<Record<BillingReadinessCategory, HTMLButtonElement | null>>
  >({});
  const clearOpenTimer = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [clearOpenTimer, clearCloseTimer]
  );

  const handleOpenChange = useCallback(
    (category: BillingReadinessCategory) => (next: boolean) => {
      clearOpenTimer();
      clearCloseTimer();
      if (next) {
        pinned.current = true;
        setOpenCategory(category);
        setOpenSource('persistent');
      } else {
        pinned.current = false;
        setOpenSource(null);
        setOpenCategory(null);
      }
    },
    [clearOpenTimer, clearCloseTimer]
  );

  const openHover = useCallback(
    (category: BillingReadinessCategory) => () => {
      clearOpenTimer();
      clearCloseTimer();
      // Never downgrade an already-open (or pinned) panel back to hover, which
      // would let a later pointer exit close a pinned panel.
      if (pinned.current) return;
      openTimer.current = setTimeout(() => {
        openTimer.current = null;
        setOpenSource('hover');
        setOpenCategory(category);
      }, 300);
    },
    [clearOpenTimer, clearCloseTimer]
  );

  const closeSoon = useCallback(() => {
    clearOpenTimer();
    if (pinned.current) return;
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      setOpenSource(null);
      setOpenCategory(null);
      closeTimer.current = null;
    }, 150);
  }, [clearOpenTimer, clearCloseTimer]);

  const pin = useCallback(
    (category: BillingReadinessCategory) => () => {
      clearOpenTimer();
      if (pinned.current && openCategory === category) {
        handleOpenChange(category)(false);
        return;
      }
      clearCloseTimer();
      pinned.current = true;
      setOpenSource('persistent');
      setOpenCategory(category);
      // An already-mounted hover panel does not rerun Radix's autofocus.
      if (openSource === 'hover' && openCategory === category) {
        contentRef.current?.focus();
      }
    },
    [
      clearOpenTimer,
      clearCloseTimer,
      handleOpenChange,
      openCategory,
      openSource,
    ]
  );

  return (
    <div className='flex flex-wrap items-center gap-1.5'>
      {categories.map(({ category, label, state }) => {
        const isOpen = openCategory === category;
        return (
          <Popover
            key={category}
            modal={false}
            open={isOpen}
            onOpenChange={handleOpenChange(category)}
          >
            <PopoverTrigger asChild>
              <button
                ref={node => {
                  triggerRefs.current[category] = node;
                }}
                type='button'
                aria-label={`${label}: ${STATE_TEXT[state]}`}
                aria-expanded={isOpen}
                className='inline-flex w-fit cursor-pointer items-center gap-1 rounded-sm px-1 py-0.5 text-[11px] font-medium text-foreground outline-offset-2 hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring'
                onClick={event => {
                  // Own the toggle: the first click pins hover content, and
                  // the second closes it. Suppress Radix's additional toggle.
                  event.preventDefault();
                  event.stopPropagation();
                  pin(category)();
                }}
                onKeyDown={event => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  event.stopPropagation();
                  pin(category)();
                }}
                onMouseEnter={openHover(category)}
                onMouseLeave={closeSoon}
              >
                <StateIcon state={state} />
                <span>{label}</span>
              </button>
            </PopoverTrigger>
            {isOpen ? (
              <PopoverContent
                ref={contentRef}
                side='bottom'
                align='start'
                className='max-h-[min(24rem,var(--radix-popover-content-available-height))] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto p-3'
                onOpenAutoFocus={event => {
                  // A hover-open panel must not take keyboard focus; a pinned
                  // one keeps Radix's focus so Tab reaches its links.
                  if (openSource === 'hover') event.preventDefault();
                }}
                onEscapeKeyDown={() => {
                  returnFocusOnClose.current = true;
                }}
                onCloseAutoFocus={event => {
                  if (returnFocusOnClose.current) {
                    returnFocusOnClose.current = false;
                    // Restore the exact category trigger after Escape.
                    event.preventDefault();
                    triggerRefs.current[category]?.focus();
                    return;
                  }
                  // Outside dismissal must not pull focus back from the
                  // destination the user clicked.
                  event.preventDefault();
                }}
                onClick={event => event.stopPropagation()}
                onMouseEnter={() => {
                  clearOpenTimer();
                  clearCloseTimer();
                }}
                onMouseLeave={closeSoon}
              >
                <div className='text-xs leading-relaxed [&>ul>li+li]:border-t [&>ul>li+li]:border-border [&>ul>li+li]:pt-2'>
                  <ClaimReadinessDetails
                    entry={entry}
                    scope={scope}
                    category={category}
                  />
                </div>
              </PopoverContent>
            ) : null}
          </Popover>
        );
      })}
    </div>
  );
};

export default ClaimReadinessCell;
